import { h } from "@stencil/core";
import serializeForm from "../../utils/serializeForm";
import {
    CheckboxFilterBlock,
    DropdownFilterBlock,
    FilterBlock,
    FilterBlockTypesEnum,
    SelectFilterBlock,
} from "../../apis/stateful-content";
import { TranslateFn } from "../stateful-content/stateful-content-utils";

export const getActiveFilterCount = (query: string) => {
    if (query === "") {
        return;
    }
    // split query into query parts and only count the ones that are not empty
    const queryParts = query.split("&");
    const rangeStart = document.querySelector(
        "ux-iws-stateful-filter"
    ).rangeStartName;
    const rangeEnd = document.querySelector(
        "ux-iws-stateful-filter"
    ).rangeEndName;
    const hasRangeStart = queryParts.some(
        (part) => part.split("=")[0] === rangeStart
    );
    const hasRangeEnd = queryParts.some(
        (part) => part.split("=")[0] === rangeEnd
    );
    let activeFilterCount = 0;
    queryParts.forEach((part) => {
        const [name, value] = part.split("=");
        if (
            document.querySelector(`[name='${name}']`)?.closest("ux-filter") ===
            null
        ) {
            return;
        }
        if (value !== "") {
            activeFilterCount++;
        }
    });
    if (hasRangeStart && hasRangeEnd) {
        activeFilterCount--;
    }
    return activeFilterCount;
};

const columnClassName =
    "ux-col-12 ux-col-sm-6 ux-col-lg-4 ux-col-xl-3 ux-u-mb-xs";

export const parseFilterData = (form: HTMLFormElement): string => {
    return form !== null ? serializeForm(form) : "";
};

export const renderCheckbox = (
    filterBlock: CheckboxFilterBlock,
    translate: TranslateFn = () => "",
    className: string = ""
) => {
    const checkbox = filterBlock.filterItems.map((item) => {
        const inputName = item.name || filterBlock.name;
        const nameSuffix = inputName === filterBlock.name ? "[]" : "";
        const translationKey = `${inputName}.label`;
        const translated = translate(translationKey);
        const checkboxLabel =
            translated !== translationKey
                ? translated
                : item.label || item.value;
        const count = item.count !== undefined ? ` (${item.count})` : "";

        return (
            <ux-checkbox
                key={`${inputName}-${item.value}`}
                name={`${inputName}${nameSuffix}`}
                data-testid={inputName}
                value={item.value}
                label={`${checkboxLabel}${count}`}
                checked={item.active}
                disabled={item.disabled || filterBlock.disabled}
            />
        );
    });

    return <div class={className || columnClassName}>{checkbox}</div>;
};

export const renderDropdown = (
    filterBlock: DropdownFilterBlock,
    translate: TranslateFn = () => "",
    className: string = ""
) => {
    const placeholderTranslationKey = `${filterBlock.name}.placeholder`;
    const placeholderTranslated = translate(placeholderTranslationKey);
    const placeholder =
        placeholderTranslated !== placeholderTranslationKey
            ? placeholderTranslated
            : filterBlock.placeholder || filterBlock.name;

    const items = filterBlock.filterItems.map((item) => {
        const translationKey = `${filterBlock.name}.values.${item.value}`;
        const name = item.name || filterBlock.name;
        const translated = translate(translationKey);
        const checkboxLabel =
            translated !== translationKey
                ? translated
                : item.label || item.value;
        const count = item.count !== undefined ? ` (${item.count})` : "";

        return (
            <ux-checkbox-list-item
                key={`${name}-${item.value}`}
                name={`${name}${name === filterBlock.name ? "[]" : ""}`}
                value={item.value}
                label={`${checkboxLabel}${count}`}
                checked={item.active}
                disabled={item.disabled || filterBlock.disabled}
            />
        );
    });

    const searchPlaceholder =
        translate(`${filterBlock.name}.searchPlaceholder`) || "";

    return (
        <div class={className || columnClassName}>
            <ux-dropdown
                key={filterBlock.filterItems.reduce((acc, item) => {
                    return acc + (item.active ? "x" : "o");
                }, "")}
                data-testid={filterBlock.name}
                filterable={filterBlock.filterable}
                search-placeholder={searchPlaceholder}
                placeholder={placeholder}
            >
                {items}
            </ux-dropdown>
        </div>
    );
};

export const renderSingleSelect = (
    filterBlock: SelectFilterBlock,
    translate: TranslateFn = () => "",
    className: string = ""
) => {
    const items = filterBlock.options.map((option) => {
        const translationKey = `${filterBlock.name}.values.${option.value}`;
        const translated = translate(translationKey);

        const optionLabel =
            translated !== translationKey
                ? translated
                : option.label || option.value;
        const count = option.count !== undefined ? ` (${option.count})` : "";

        return (
            <option value={option.value} selected={option.selected}>
                {optionLabel}
                {count}
            </option>
        );
    });

    return (
        <div class={className || columnClassName}>
            <ux-select name={filterBlock.name} data-testid={filterBlock.name}>
                {items}
            </ux-select>
        </div>
    );
};

export const renderMultiSelect = (
    filterBlock: SelectFilterBlock,
    translate: TranslateFn = () => "",
    className: string = ""
) => {
    const placeholderTranslationKey = `${filterBlock.name}.placeholder`;
    const placeholderTranslated = translate(placeholderTranslationKey);
    const placeholder =
        placeholderTranslated !== placeholderTranslationKey
            ? placeholderTranslated
            : filterBlock.placeholder || filterBlock.name;
    const items = filterBlock.options.map((option) => {
        const translationKey = `${filterBlock.name}.values.${option.value}`;
        const translated = translate(translationKey);

        const optionLabel =
            translated !== translationKey
                ? translated
                : option.label || option.value;
        const count = option.count !== undefined ? ` (${option.count})` : "";

        return (
            <ux-checkbox-list-item
                key={`${filterBlock.name}-${option.value}`}
                name={`${filterBlock.name}[]`}
                value={option.value}
                label={`${optionLabel}${count}`}
                checked={option.selected}
                disabled={option.disabled || filterBlock.disabled}
            />
        );
    });

    return (
        <div class={className || columnClassName}>
            <ux-dropdown
                data-testid={filterBlock.name}
                key={filterBlock.options.reduce(
                    (acc, item) => acc + (item.selected ? "x" : "o"),
                    ""
                )}
                placeholder={placeholder}
            >
                {items}
            </ux-dropdown>
        </div>
    );
};

export const renderSelect = (
    filterBlock: SelectFilterBlock,
    translate: TranslateFn = () => "",
    className: string = ""
) => {
    return filterBlock.multiple
        ? renderMultiSelect(filterBlock, translate, className)
        : renderSingleSelect(filterBlock, translate, className);
};

export const renderFilterBlock = (
    filterBlock: FilterBlock,
    translate?: TranslateFn,
    className?: string
) => {
    switch (filterBlock.type) {
        case FilterBlockTypesEnum.Select:
            return renderSelect(
                filterBlock as SelectFilterBlock,
                translate,
                className
            );
        case FilterBlockTypesEnum.Dropdown:
            return renderDropdown(
                filterBlock as DropdownFilterBlock,
                translate,
                className
            );
        case FilterBlockTypesEnum.Checkbox:
            return renderCheckbox(
                filterBlock as CheckboxFilterBlock,
                translate,
                className
            );
        default:
            return null;
    }
};
